import axios, { AxiosError } from 'axios';
import { stringify } from 'query-string';
import {
  BaseEntityService,
  ErrorApiResponse,
  ID,
  ListApiResponse,
  QueryFilters,
  SingleApiResponse,
  SuccessApiResponse
} from '@ianesthesie-kit/services';
import {
  AnesthesiaType,
  FollowUpSurgicalEventPayload,
  MedicalDocument,
  PatientId,
  Priority
} from '@/modules/surgicalEvent';
import { AppConfigService } from '@/config';
// eslint-disable-next-line import/no-duplicates
import { parse } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import { enUS as enLocale, fr as frLocale } from 'date-fns/locale';
import {
  AllergicSubstance,
  Patient,
  PatientIdentityChangeOutcome,
  PatientSearchResult,
  Surgery,
  SurgicalEvent
} from './model';
import {
  FetchTemplatePayload,
  GenerateTemplatePayload,
  TemplateEditorData,
  UpsertTemplatePayload
} from '@/modules/surgicalEvent/model/medicalTemplate.model';
import { formatDateOrNull } from '@ianesthesie-kit/core';
import { MedicalTemplateType } from '@/modules/appContext';

export type SearchPatientPayload = {
  searchKey: string;
  limit?: number;
};

export type IdentityChangePreviewPayload = PatientId &
  Pick<Patient, 'birthDate' | 'gender'>;

export class SurgicalEventService extends BaseEntityService<SurgicalEvent> {
  private static readonly INSTANCE: SurgicalEventService =
    new SurgicalEventService();

  private constructor() {
    super(`/bff/doctor/patient`);
  }

  public static getInstance(): SurgicalEventService {
    return this.INSTANCE;
  }

  public searchPatients(
    payload: SearchPatientPayload
  ): Promise<ListApiResponse<PatientSearchResult>> {
    if (!payload?.searchKey) {
      return Promise.resolve({
        ok: true,
        count: 0,
        data: []
      });
    }
    const { limit = 10, searchKey } = payload;

    const q = stringify({ limit, q: this.formatDateIfApplicable(searchKey) });
    return axios
      .get<ListApiResponse<PatientSearchResult>>(
        `${this.getUrl()}/surgical-event/active/search?${q}`
      )
      .then((r) => r.data);
  }

  public fetchActive(patientId: ID): Promise<SingleApiResponse<SurgicalEvent>> {
    return axios
      .get<SingleApiResponse<SurgicalEvent & any>>(
        `${this.getUrl()}/${patientId}/surgical-event/active`
      )
      .then((r) => this.transformEventResponse(r.data))
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public createFollowUp({
    sourceSurgicalEventId,
    ...payload
  }: FollowUpSurgicalEventPayload & { sourceSurgicalEventId: ID }): Promise<
    SingleApiResponse<SurgicalEvent>
  > {
    return axios
      .post<SingleApiResponse<SurgicalEvent>>(
        `${this.getUrl()}/surgical-event/${sourceSurgicalEventId}/follow-up`,
        {
          ...payload
        }
      )
      .then((r) => r.data)
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public updatePriority(
    priority: Priority,
    patientId: ID
  ): Promise<SingleApiResponse<SurgicalEvent>> {
    return axios
      .put(`${this.getUrl()}/${patientId}/surgical-event/active/priority`, {
        priority
      })
      .then((r) => this.transformEventResponse(r.data))
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public updateStaffComents(
    content: string,
    patientId: ID
  ): Promise<SingleApiResponse<SurgicalEvent>> {
    return axios
      .put(`${this.getUrl()}/${patientId}/surgical-event/active/staff-notes`, {
        content
      })
      .then((r) => this.transformEventResponse(r.data))
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public updateContextNotes(
    content: string,
    patientId: ID
  ): Promise<SingleApiResponse<SurgicalEvent>> {
    return axios
      .put(
        `${this.getUrl()}/${patientId}/surgical-event/active/context-notes`,
        {
          content
        }
      )
      .then((r) => this.transformEventResponse(r.data))
      .catch((err: AxiosError<ErrorApiResponse>) => {
        throw err.response?.data?.error || JSON.stringify(err);
      });
  }

  public generateCompleteReport(
    patientId: ID
  ): Promise<{ data: MedicalDocument }> {
    return axios
      .post(
        `${this.getUrl()}/${patientId}/surgical-event/active/complete-report`
      )
      .then((resp) => ({
        data: resp.data.data
      }));
  }

  public generateTemplate({
    patientId,
    template,
    html,
    attachments
  }: GenerateTemplatePayload): Promise<{ data: MedicalDocument }> {
    return axios
      .post(
        `${this.getUrl()}/${patientId}/surgical-event/active/template/${template}`,
        { html, attachments }
      )
      .then((resp) => ({
        data: resp.data.data
      }));
  }

  public fetchTemplateData({
    patientId,
    template
  }: FetchTemplatePayload): Promise<{
    data: TemplateEditorData[];
  }> {
    if (template === MedicalTemplateType.PREOP_EVAL_PER_HOSPITAL) {
      return Promise.resolve({
        data: []
      });
    }
    return axios
      .get<ListApiResponse<TemplateEditorData>>(
        `${this.getUrl()}/${patientId}/surgical-event/active/template/${template}/data`
      )
      .then((resp) => ({
        data: resp.data.data
      }));
  }

  public upsertTemplateData({
    patientId,
    template,
    editorId,
    content
  }: UpsertTemplatePayload): Promise<{ data: TemplateEditorData }> {
    return axios
      .post(
        `${this.getUrl()}/${patientId}/surgical-event/active/template/${template}/data`,
        { textEditorId: editorId, content }
      )
      .then((resp) => ({
        data: resp.data.data
      }));
  }

  public async generatePreopEvalPerHospital({
    patientId
  }: PatientId): Promise<void> {
    // Get the patientsAppIntegrationEnabled setting and appStore
    const appStore = await import('@/store').then((mod) => mod.appStore);
    const { doctorBffApi: api } = await import('@/modules');
    const state = appStore.getState();
    const userConfig = api.endpoints.userConfig.select()(state).data;
    const patientsAppIntegrationEnabled =
      userConfig?.settings?.patientsAppIntegrationEnabled ?? true;

    const blob = (
      await axios.post(
        `${this.getUrl()}/${patientId}/surgical-event/active/template/preop-eval?patientsAppIntegrationEnabled=${patientsAppIntegrationEnabled}`,
        {},
        { responseType: 'blob' }
      )
    ).data;
    let name = await import('./surgicalEvent.slice')
      .then((mod) => mod.surgicalEventApi)
      .then(
        (seApi) =>
          seApi.endpoints.activeSurgicalEvent.select({ patientId })(
            appStore.getState()
          ).data
      )
      .then((se) => {
        if (!se) {
          return undefined;
        }
        return `clinicalNote_${se.patient.lastName}_${se.patient.firstName}`;
      });

    if (!name) {
      name = 'clinicalNote';
    }

    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = objUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objUrl);
  }

  public fetchSurgeryGlobalList(): Promise<ListApiResponse<Surgery>> {
    return axios
      .get<ListApiResponse<Surgery>>(`${this.getUrl()}/surgical-event/surgery`)
      .then((r) => r.data);
  }

  public fetchAnesthesiaTypeGlobalList(): Promise<
    ListApiResponse<AnesthesiaType>
  > {
    return axios
      .get<ListApiResponse<AnesthesiaType>>(
        `${this.getUrl()}/surgical-event/anesthesia-type`
      )
      .then((r) => r.data);
  }

  public fetchAllergicSubstanceGlobalList(): Promise<
    ListApiResponse<AllergicSubstance>
  > {
    return axios
      .get<ListApiResponse<AllergicSubstance>>(
        `${this.getUrl()}/surgical-event/allergic-substance`
      )
      .then((r) => r.data);
  }

  public fetchSurgeonNamesGlobalList(): Promise<ListApiResponse<string>> {
    return axios
      .get<ListApiResponse<string>>(
        `${this.getUrl()}/surgical-event/surgeon-name`
      )
      .then((r) => r.data);
  }

  public deleteSurgeonNameFromGlobalList(
    surgeonName: string
  ): Promise<SuccessApiResponse> {
    return axios
      .delete<SuccessApiResponse>(
        `${this.getUrl()}/surgical-event/surgeon-name?surgeon=${surgeonName}`
      )
      .then((r) => r.data);
  }

  public patchPatient(
    patient: Partial<Patient> & { id: ID }
  ): Promise<SingleApiResponse<Patient>> {
    return axios
      .patch<SingleApiResponse<Patient>>(`${this.getUrl()}`, patient)
      .then((r) => r.data);
  }

  /** Tells what saving this birth date and sex would do to the patient's questionnaire; saves nothing. */
  public previewIdentityChange({
    patientId,
    ...change
  }: IdentityChangePreviewPayload): Promise<
    SingleApiResponse<PatientIdentityChangeOutcome>
  > {
    return axios
      .post<SingleApiResponse<PatientIdentityChangeOutcome>>(
        `${this.getUrl()}/${patientId}/identity-change/preview`,
        change
      )
      .then((r) => r.data);
  }

  public fetchAll(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    filters?: QueryFilters
  ): Promise<ListApiResponse<SurgicalEvent>> {
    throw new Error('Unsupported operation');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public fetchById(id: ID): Promise<SingleApiResponse<SurgicalEvent>> {
    throw new Error('Unsupported operation');
  }

  public create(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    newItem: Partial<SurgicalEvent>
  ): Promise<SingleApiResponse<SurgicalEvent>> {
    throw new Error('Unsupported operation');
  }

  public update(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    item: SurgicalEvent
  ): Promise<SingleApiResponse<SurgicalEvent>> {
    throw new Error('Unsupported operation');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public delete(item: ID | SurgicalEvent): Promise<SuccessApiResponse> {
    throw new Error('Unsupported operation');
  }

  private formatDateIfApplicable(originalSearchKey: string) {
    let searchKey = originalSearchKey.trim().replaceAll(/[-/.]/g, '/');
    // dd/dd/dddd dd/dd/dd dddddddd
    if (/^\d{1,2}\/?\d{1,2}((\/(\d{2}|\d{4}))|\d{4})$/.test(searchKey)) {
      if (searchKey.length === 8 && !searchKey.includes('/')) {
        searchKey = `${searchKey.slice(0, 2)}/${searchKey.slice(
          2,
          4
        )}/${searchKey.slice(4)}`;
      }

      try {
        const date = parse(searchKey, 'P', new Date(), {
          locale:
            AppConfigService.getInstance().countryBasedOnEnv === 'US'
              ? enLocale
              : frLocale
        });
        return formatDateOrNull(date) ?? originalSearchKey;
      } catch {
        return originalSearchKey;
      }
    }

    return originalSearchKey;
  }

  private transformEventResponse(
    resp: SingleApiResponse<SurgicalEvent & any>
  ): SingleApiResponse<SurgicalEvent> {
    if (!resp) {
      return resp;
    }

    return {
      ...resp,
      data: {
        ...resp.data,
        patient: {
          ...resp.data.patient,
          facePicture: resp.data.patient.facePictureMedium
        }
      }
    };
  }
}
